import React, { useState } from 'react';
import { computeBendingStress } from '../utility/calculate_bending_stress';
import { computeTensionFromGrams } from '../utility/calculate_tension';
import  { ROUNDING_VALUES } from '../constants/calculator_constants';

import NumberField from './number-field';

const FiberStressCalculator: React.FC = () => {
  const [claddingDiameter, setCladdingDiameter] = useState<number | string>(125);
  const [bendRadius, setBendRadius] = useState<number | string>(0);
  const [tensionGrams, setTensionGrams] = useState<number | string>(0);
  const [stressKpsi, setStressKpsi] = useState<number | string>(0);
  const [stressGPa, setStressGPa] = useState<number | string>(0);

  // Error states
  const [errors, setErrors] = useState({
    claddingDiameter: '',
    bendRadius: '',
    tensionGrams: ''
  });

  const validateInput = (value: string | number, fieldName: string, min: number = 0, max: number = Infinity): string => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    
    if (value === '' || value === null || value === undefined) {
      return '';
    }
    
    if (isNaN(numValue)) {
      return `${fieldName} must be a valid number`;
    }
    
    if (numValue < min) {
      return `${fieldName} must be greater than or equal to ${min}`;
    }
    
    if (numValue > max) {
      return `${fieldName} must be less than or equal to ${max}`;
    }
    
    return '';
  };

  const updateError = (field: string, error: string) => {
    setErrors(prev => ({ ...prev, [field]: error }));
  };


  const calculateTensionStress = () => {
    const fibercladdingDiameter = getCladdingDiameter();
    const tensionDiameter = getTensionGrams();
    const result = computeTensionFromGrams(tensionDiameter, fibercladdingDiameter);

    return {
      kpsi: result.kpsi || 0,
      gpa: result.gpa || 0
    };
  };

  const calculateBendingStress = () => {
    const fibercladdingDiameter = getCladdingDiameter();
    const bendRadius = getBendRadius();
    const result = computeBendingStress(bendRadius, fibercladdingDiameter);

    return {
      kpsi: result.rawKpsi || 0,
      gpa: result.rawgpa || 0
    };
  };


  const getCladdingDiameter = (): number => {
    const value = typeof claddingDiameter === 'number' ? claddingDiameter : parseFloat(claddingDiameter);
    return isNaN(value) ? 125 : value;
  };

  const getBendRadius = (): number => {
    const value = typeof bendRadius === 'number' ? bendRadius : parseFloat(bendRadius);
    return isNaN(value) ? 0 : value;
  };

 const getTensionGrams = (): number => {
    const value = typeof tensionGrams === 'number' ? tensionGrams : parseFloat(tensionGrams);
    return isNaN(value) ? 0 : value;
  }

  const setFieldsToZero = () => {
    setBendRadius(0);
    setTensionGrams(0);
    setStressKpsi('0');
    setStressGPa('0');
  }

  const handleCladdingDiameterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(/[^\d.]/g, '');
      const error = validateInput(value, 'Cladding Diameter', 0, 10000000);
      updateError('claddingDiameter', error);
      
      const v = parseFloat(value);
      if (!isNaN(v) && error === '') {
        setCladdingDiameter(v);
      } else {
        setCladdingDiameter(value);
      }
      setFieldsToZero();
  }

  const handleBendRadiusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const error = validateInput(value, 'Bend Radius', 0, 10000000);
      updateError('bendRadius', error);
      
      const v = parseFloat(value);
      if (!isNaN(v) && error === '') {
        setBendRadius(v);
        const fibercladdingDiameter = getCladdingDiameter();
        setCladdingDiameter(fibercladdingDiameter);
        const result = computeBendingStress(Number(v), fibercladdingDiameter);
        const { rawKpsi = 0, rawgpa = 0 } = result;

        const { kpsi: kpsi_tension = 0, gpa: gpa_tension = 0 } = calculateTensionStress();
        const totalKpsi = rawKpsi + kpsi_tension;
        const totalGpa = rawgpa + gpa_tension;
        
        if (isNaN(totalKpsi) || isNaN(totalGpa)) {
          setStressKpsi('NaN');
          setStressGPa('NaN');
        } else {
          setStressKpsi(totalKpsi.toFixed(ROUNDING_VALUES.STRESS_KPSI));
          setStressGPa(totalGpa.toFixed(ROUNDING_VALUES.STRESS_GPA));
        }
      } else {
        setBendRadius(value);
      }
      
      if (error !== '') {
        return;
      }
      
      if (value === '' || isNaN(v)) {
          setBendRadius('');

          const { kpsi: kpsi_tension, gpa: gpa_tension } = calculateTensionStress();

          if (isNaN(kpsi_tension) || isNaN(gpa_tension)) {
              setStressKpsi('');
              setStressGPa('');
              return;
          }
          setStressKpsi(Number(kpsi_tension) === 0 ? '0' : Number(kpsi_tension).toFixed(ROUNDING_VALUES.STRESS_KPSI));
          setStressGPa(Number(gpa_tension) === 0 ? '0' : Number(gpa_tension).toFixed(ROUNDING_VALUES.STRESS_GPA));
      }
    };

  const handleTensionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const error = validateInput(value, 'Wind Tension', 0, 10000000);
      updateError('tensionGrams', error);
      
      const v = parseFloat(value);
      if (!isNaN(v) && error === '') {
        setTensionGrams(v);
        const fibercladdingDiameter = getCladdingDiameter();
        setCladdingDiameter(fibercladdingDiameter);
        const { kpsi, gpa} = computeTensionFromGrams(v, fibercladdingDiameter);

        const { kpsi: kpsi_bend, gpa: gpa_bend } = calculateBendingStress();
        setStressKpsi((Number(kpsi) + Number(kpsi_bend)).toFixed(ROUNDING_VALUES.STRESS_KPSI));

        setStressGPa((Number(gpa) + Number(gpa_bend)).toFixed(ROUNDING_VALUES.STRESS_GPA));
      } else {
        setTensionGrams(value);
      }
      
      if (error !== '') {
        return;
      }
      
      if (value === '' || isNaN(v)) {
          setTensionGrams('');
          
          const { kpsi: kpsi_bend, gpa: gpa_bend } = calculateBendingStress();

          if (isNaN(Number(kpsi_bend)) || isNaN(Number(gpa_bend))) {
              setStressKpsi('');
              setStressGPa('');
              return;
          }
          setStressKpsi(Number(kpsi_bend) === 0 ? 0 : Number(kpsi_bend).toFixed(ROUNDING_VALUES.STRESS_KPSI));
          setStressGPa(Number(gpa_bend) === 0 ? 0 : Number(gpa_bend).toFixed(ROUNDING_VALUES.STRESS_GPA));
      }
  }
  
  return (
    <div className="flex flex-col gap-8 px-4 py-8 sm:p-8 bg-[#f2f4f7] rounded-xl" aria-label="Fiber wound in tension stress calculator">
      <div className="w-full">
        <h3 className="mb-5 font-theSans text-[1.3125rem] leading-[120%] tracking-0 text-black">Fiber wound in tension stress calculator</h3>
        <p className="text-[1rem] text-[#101114] leading-[150%]">This calculator can be used to estimate the maximum stress on a fiber coiled with a specific winding tension. The calculator updates the results fields when input values are entered.</p>
      </div>
      <form className="flex flex-col gap-6 w-full" onSubmit={(e) => e.preventDefault()}>
        <fieldset className="flex flex-col gap-6">
          <legend className="block float-left m-0 whitespace-normal font-bold text-[1.125rem] leading-[120%]">
            <span className="block">Input Values</span>
          </legend>
          <div className="flex flex-col md:flex-row gap-6 md:gap-4">
            {/* Cladding Diameter */}
            <NumberField
              label="Cladding Diameter"
              value={claddingDiameter}
              onChange={handleCladdingDiameterChange}
              unit="µm"
              title="Enter the fiber cladding diameter in micrometers"
              errors={errors?.claddingDiameter}
            />
            {/* Bend Radius */}
            <NumberField
              label="Bend Radius"
              value={bendRadius}
              onChange={handleBendRadiusChange}
              unit="mm"
              title="Enter a positive number for bend radius in millimeters"
              errors={errors?.bendRadius}
            />
            {/* Wind Tension */}
            <NumberField
              label="Wind Tension"
              value={tensionGrams}
              onChange={handleTensionChange}
              unit="g"
              title="Enter a positive number for winding tension in grams"
              errors={errors?.tensionGrams}
            />
          </div>
        </fieldset>
        <fieldset className="flex flex-col gap-6">
          <legend className="block float-left m-0 whitespace-normal font-bold text-[1.125rem] leading-[120%]">
            <span className="block">Results</span>
          </legend>
          <div className="flex flex-col md:flex-row gap-6 md:gap-4">
            {/* Maximum Stress */}
            <NumberField
              label="Maximum Stress"
              value={stressKpsi}
              unit="kpsi"
              title="Calculated maximum stress in kilopounds per square inch"
              locked={true}
            />
            {/* Bend Radius */}
            <NumberField
              label="Maximum Stress"
              value={stressGPa}
              unit="GPa"
              title="Calculated maximum stress in gigapascals"
              locked={true}
            />
          </div>
        </fieldset>
      </form>
    </div>
  );
};

export default FiberStressCalculator;
        